/**
 * usePropertyDeals — the property detail page's Transactions module.
 *
 * Wraps GET /api/properties/:id/deals: this property's sale / lease
 * allocations, plus those of its spaces and snapshots, each with its
 * transaction row embedded (`transaction`) and the number of properties
 * that transaction spans (`transaction_property_count`, "bundled" when > 1).
 * Newest deal first (sale close_date / lease execution_date, then ids).
 *
 * It replaces reading the first 500 transactions and the first 1000
 * allocations of each kind and matching this property's deals client-side,
 * which lost deals once the tables outgrew those windows (scale finding F5).
 *
 * `includeArchived` (a feature:can_archive_data holder only — the API answers
 * 403 otherwise) asks `?archived=include`: the deals whose transaction is
 * archived come back too, in `archived: { sales, leases }`, each row
 * `archived: true` (the page's Archived leases / sales section). It is part of
 * the query key.
 *
 * Freshness: the key lives outside the generated table keys, so a
 * transaction / allocation mutation's own invalidation does not reach it —
 * main.tsx's MutationCache invalidates `propertyDealsKeys.all` after every
 * successful mutation instead (active queries refetch; inactive ones are
 * marked stale).
 */

import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../../http-client';
import type { SaleTransactionsRow } from '../../../types/generated_schemas/generated-sale_transactions-schema';
import type { LeaseTransactionsRow } from '../../../types/generated_schemas/generated-lease_transactions-schema';
import type { SaleTransactionPropertiesRow } from '../../../types/generated_schemas/generated-sale_transaction_properties-schema';
import type { LeaseTransactionPropertiesRow } from '../../../types/generated_schemas/generated-lease_transaction_properties-schema';

export type SaleDealRow = SaleTransactionPropertiesRow & {
  transaction: SaleTransactionsRow | null;
  transaction_property_count: number;
  /** Only on a row of `archived`: its transaction is archived. */
  archived?: true;
};
export type LeaseDealRow = LeaseTransactionPropertiesRow & {
  transaction: LeaseTransactionsRow | null;
  transaction_property_count: number;
  archived?: true;
};

export interface PropertyDeals {
  sales: SaleDealRow[];
  leases: LeaseDealRow[];
  /** Only with `includeArchived`. */
  archived?: { sales: SaleDealRow[]; leases: LeaseDealRow[] };
}

interface PropertyDealsResponse {
  success: boolean;
  data: PropertyDeals;
}

export const propertyDealsKeys = {
  all: ['property-deals'] as const,
  byProperty: (propertyId: string, includeArchived = false) =>
    ['property-deals', propertyId, { includeArchived }] as const,
};

export function usePropertyDeals(propertyId: string, { includeArchived = false }: { includeArchived?: boolean } = {}) {
  return useQuery({
    queryKey: propertyDealsKeys.byProperty(propertyId, includeArchived),
    queryFn: async () => {
      const res = await httpClient.get<PropertyDealsResponse>(
        `/properties/${encodeURIComponent(propertyId)}/deals${includeArchived ? '?archived=include' : ''}`,
      );
      return res.data;
    },
    enabled: !!propertyId,
  });
}
